import { useState } from "react";
import * as api from "../api.js";
import { useAcoes, useEstado, usePode } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import {
  dataHora,
  diaCurto,
  diaDe,
  hojeDe,
  hora,
  ICONES,
  Icone,
  mmss,
  momentoDoCampo,
  paraCampoDeMomento,
  s,
} from "../visual.jsx";
import { Botao, MenuDeAcoes } from "./comuns.jsx";
import { confirmar, pedirTexto } from "@/componentes/pergunta.tsx";

/**
 * As reuniões deste caso — a lista, não o conteúdo.
 *
 * Era uma ABA inteira ("Reunião") que, na maioria dos casos, tinha um botão e
 * 2000px de vazio. Virou seção da ficha porque é isso que ela é: alinhamento é
 * dado DO CASO. Abrir uma reunião empurra a coluna para o detalhe dela
 * (`?gravacao=`), em vez de estufar a lista com transcrição, vídeo e marcadores
 * dentro de um acordeão de 300px de largura — que era onde a transcrição de
 * quarenta minutos vivia.
 *
 * O gravador não está aqui: ele mora no alto da coluna (`Painel.jsx`), onde a
 * faixa de "GRAVANDO" fica à vista de qualquer altura da rolagem.
 */
/**
 * A reunião MARCADA — data, sala e o convite do cliente.
 *
 * A tabela `reunioes` existia desde sempre e nenhuma tela a lia: a fila que a
 * confirmação de tese abre sozinha, a data e agora o link da chamada viviam só
 * no banco e na agenda. Quem atendia via as GRAVAÇÕES (o que já aconteceu) e não
 * tinha como ver nem marcar o que vai acontecer — a data ia para o calendário de
 * alguém e o link para uma mensagem colada à mão.
 *
 * A SALA nasce com a marcação, no servidor (`fluxo.salaDaReuniao`): ninguém cria
 * reunião em outro lugar para colar aqui. Avisar o cliente é BOTÃO e não
 * automático — mandar mensagem para fora é o lado do portão que espera gente.
 */
function Marcada({ reuniao, nome }) {
  const { avisar, carregarCaso } = useAcoes();
  const pode = usePode();
  const [ocupado, setOcupado] = useState("");
  /* Aplicada é reunião que já decidiu o caso: remarcar desfaria a aplicação que
     libera a elaboração (o servidor recusa), e avisar ou apagar não têm mais
     sentido. Fica só a leitura. */
  const aplicada = reuniao.estado === "aplicada";
  const podeMarcar = pode("alinhamento:marcar") && !aplicada;
  const podeAvisar = Boolean(reuniao.quando) && pode("leads:mensagem") && !aplicada;

  const mexer = async (marca, fn, recado) => {
    setOcupado(marca);
    try {
      await fn();
      await carregarCaso(reuniao.record_id);
      if (recado) avisar(recado);
    } catch (e) {
      avisar(e.message);
    } finally {
      setOcupado("");
    }
  };

  const marcar = async () => {
    const quando = await pedirData(reuniao.quando);
    if (!quando) return;
    await mexer("marcar", () => api.marcarReuniao(reuniao.id, quando), "Reunião marcada.");
  };

  const avisarCliente = async () => {
    if (
      !(await confirmar({
        titulo: `Mandar o link para ${nome}?`,
        texto: "Sai uma mensagem no WhatsApp dele com a data e o endereço da sala. Ele não precisa instalar nada.",
        acao: "Mandar o convite",
      }))
    )
      return;
    await mexer("avisar", () => api.avisarDaReuniao(reuniao.id), "Convite enviado ao cliente.");
  };

  const apagar = async () => {
    if (
      !(await confirmar({
        titulo: "Apagar esta reunião?",
        texto: "A data, os participantes e a sala somem. As gravações ficam — elas são de conversas que aconteceram de verdade.",
        acao: "Apagar a reunião",
        perigo: true,
      }))
    )
      return;
    await mexer("apagar", () => api.apagarReuniao(reuniao.id), "Reunião apagada.");
  };

  /*
   * UM primário, e ele muda com o momento.
   *
   * Eram cinco botões do mesmo peso — Entrar, Copiar, Mudar a data, Mandar o
   * link, Apagar — com Apagar encostado em Entrar. Antes do convite a próxima
   * coisa a fazer é mandar o link; depois dele, ou no dia, é entrar na sala. O
   * resto é de vez em quando, e mora no menu.
   */
  /* O dia é o do relógio de quem lê: no dia da reunião, ou depois dela, entrar é o que se faz. */
  const noDia = Boolean(reuniao.quando) && diaDe(reuniao.quando) <= hojeDe();
  const primario = aplicada
    ? null
    : !reuniao.quando
      ? podeMarcar
        ? "marcar"
        : null
      : podeAvisar && !reuniao.convite_em && !noDia
        ? "avisar"
        : reuniao.link
          ? "entrar"
          : null;

  const menu = [
    reuniao.link
      ? {
          chave: "copiar",
          rotulo: "Copiar o link da sala",
          icone: ICONES.encaminhar,
          /* `?.` porque `clipboard` não existe em contexto sem HTTPS: no IP da
             rede local o item continua e não derruba a coluna. */
          aoTocar: () => {
            void navigator.clipboard?.writeText(reuniao.link);
            avisar("Link da sala copiado.");
          },
        }
      : null,
    podeAvisar && primario !== "avisar"
      ? {
          chave: "avisar",
          rotulo: reuniao.convite_em ? "Mandar o link de novo" : "Mandar o link ao cliente",
          icone: ICONES.enviar,
          aoTocar: () => void avisarCliente(),
        }
      : null,
    podeMarcar && reuniao.quando
      ? { chave: "marcar", rotulo: "Mudar a data", icone: ICONES.agenda, aoTocar: () => void marcar() }
      : null,
    podeMarcar ? { chave: "apagar", rotulo: "Apagar a reunião", icone: ICONES.apagar, aoTocar: () => void apagar() } : null,
  ].filter(Boolean);

  return (
    <div data-reuniao={reuniao.id} className="mb-2 rounded-cartao border border-terra/30 bg-[color-mix(in_oklab,var(--color-terra)_5%,var(--color-card))] p-[10px_12px]">
      <div className="flex items-start gap-2.5">
        <span className="mt-[2px] flex size-6 flex-none items-center justify-center rounded-etiqueta bg-terra/12">
          <Icone de={ICONES.agenda} tamanho={14} cor="var(--color-terra-escura)" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-corpo leading-[1.35] font-semibold">
            {reuniao.quando ? dataHora(reuniao.quando) : "Reunião sem data"}
          </div>
          {reuniao.participantes ? (
            <div className="mt-[3px] text-apoio text-tinta-fraca">{reuniao.participantes}</div>
          ) : null}
          {aplicada ? (
            <div className="mt-[3px] text-apoio text-verde-fundo">aplicada ao caso</div>
          ) : reuniao.convite_em ? (
            <div className="mt-[3px] text-apoio text-verde-fundo">convite enviado {quandoDoConvite(reuniao.convite_em)}</div>
          ) : null}
        </div>
        {menu.length ? (
          <MenuDeAcoes
            rotulo="Mais sobre esta reunião"
            titulo="Copiar o link, mudar a data ou apagar"
            largura="220px"
            classe="flex size-mini flex-none cursor-pointer items-center justify-center rounded-controle border-none bg-transparent text-tinta-suave hover:bg-foreground/8 hover:text-foreground"
            itens={menu}
          >
            <Icone de={ICONES.mais} tamanho={16} />
          </MenuDeAcoes>
        ) : null}
      </div>

      {primario === "entrar" ? (
        /* Endereço, não arquivo: `<a>` que abre a sala noutra aba. */
        <a
          href={reuniao.link}
          target="_blank"
          rel="noopener noreferrer"
          /* A pele do primário da casa (tinta), à mão porque é `<a>` de sala e não
             arquivo — o `baixar` do `Botao` poria `download` nele. */
          className="mt-2 inline-flex min-h-padrao items-center gap-2 rounded-controle bg-tinta px-3.5 text-corpo font-semibold text-mesa no-underline transition-colors hover:bg-tinta/86 hover:text-mesa max-[759px]:min-h-toque"
        >
          <Icone de={ICONES.calls} tamanho={16} />
          Entrar na sala
        </a>
      ) : primario === "avisar" ? (
        <div className="mt-2">
          {/* Terra: é o que SAI para o cliente. */}
          <Botao
            rotulo="Mandar o link ao cliente"
            icone={ICONES.enviar}
            variante="acao"
            carregando={ocupado === "avisar"}
            desabilitado={Boolean(ocupado) && ocupado !== "avisar"}
            aoTocar={avisarCliente}
          />
        </div>
      ) : primario === "marcar" ? (
        <div className="mt-2">
          <Botao
            rotulo="Marcar a data"
            icone={ICONES.agenda}
            destaque
            carregando={ocupado === "marcar"}
            desabilitado={Boolean(ocupado) && ocupado !== "marcar"}
            aoTocar={marcar}
          />
        </div>
      ) : null}
    </div>
  );
}

/*
 * A data pedida no calendário da casa. `datetime-local` porque "20/08 16h"
 * digitado não é data; `paraCampoDeMomento` porque o banco guarda UTC e o campo
 * mostra o que recebe sem converter (a das 16h abriria como 19h). Volta o
 * momento LOCAL: quem converte para UTC é o servidor (`data.utcDeLocal`).
 */
async function pedirData(atual) {
  const quando = await pedirTexto({
    titulo: atual ? "Mudar a data da reunião" : "Marcar a reunião",
    texto: "A sala da chamada é criada junto, e o link fica aqui para você mandar ao cliente.",
    acao: atual ? "Mudar a data" : "Marcar",
    valor: paraCampoDeMomento(atual),
    tipo: "datetime-local",
  });
  return quando ? momentoDoCampo(quando) : null;
}

/* "às 14:02" hoje, "em 23/09 às 14:02" nos outros dias. */
function quandoDoConvite(q) {
  return diaDe(q) === hojeDe() ? `às ${hora(q)}` : `em ${diaCurto(q)} às ${hora(q)}`;
}

export default function ListaDeReunioes() {
  const { caso } = useEstado();
  const { avisar, carregarCaso } = useAcoes();
  const pode = usePode();
  const { ajustar } = useNavegacao();
  const gravacoes = caso?.gravacoes ?? [];
  const marcadas = caso?.reunioes ?? [];
  const nome = caso?.registro?.nome ?? "o cliente";

  /* Marcar é UM gesto: a data primeiro, e a reunião nasce com ela e com a sala.
     Criava-se a reunião sem data e aparecia um segundo cartão pedindo "Marcar
     data e criar a sala" — dois toques e dois cartões para uma decisão. */
  const marcarNova = async () => {
    const quando = await pedirData(null);
    if (!quando) return;
    try {
      const nova = await api.abrirReuniao(caso.registro.id);
      await api.marcarReuniao(nova.id, quando);
      avisar("Reunião marcada.");
    } catch (e) {
      avisar(e.message);
    } finally {
      await carregarCaso(caso.registro.id);
    }
  };

  return (
    <>
      {/* O QUE VAI ACONTECER vem antes do que já aconteceu: no dia da reunião a
          pergunta é "qual é o link", não "o que ficou combinado em maio". */}
      {marcadas.map((r) => (
        <Marcada key={r.id} reuniao={r} nome={nome} />
      ))}

      {/* "Nenhuma reunião ainda" só quando não há NADA — nem marcada, nem
          gravada: a frase aparecia embaixo de uma reunião marcada para amanhã. */}
      {!marcadas.length && (pode("alinhamento:marcar") || !gravacoes.length) ? (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-cartao border border-dashed border-foreground/12 p-[10px_12px]">
          <span className="min-w-0 flex-1 text-apoio leading-[1.5] text-tinta-fraca">
            {gravacoes.length ? "Nenhuma reunião marcada." : "Nenhuma reunião ainda."}
            {pode("alinhamento:marcar")
              ? ""
              : " Quem marca é o atendimento ou o advogado; o acesso se libera em Equipe e acessos."}
          </span>
          {pode("alinhamento:marcar") ? <Botao rotulo="Marcar reunião" icone={ICONES.agenda} aoTocar={marcarNova} /> : null}
        </div>
      ) : null}

      {gravacoes.length
        ? gravacoes.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => ajustar({ painel: "reunioes", gravacao: g.id })}
              data-dica="Abrir a transcrição, os combinados e os momentos marcados"
              className="lin mb-2 flex min-h-toque w-full cursor-pointer items-start gap-2.5 rounded-cartao border border-border bg-card p-[10px_12px] text-left hover:border-verde-fundo/40"
            >
              <span className="mt-[2px] flex size-6 flex-none items-center justify-center rounded-etiqueta bg-verde-fundo/12">
                <Icone de={ICONES.calls} tamanho={14} cor="var(--color-verde-fundo)" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-corpo leading-[1.35] font-semibold [overflow-wrap:anywhere]">
                  {g.assunto || g.titulo || "Reunião"}
                </span>
                {/* Data, duração e o que a reunião TEM na mesma régua monoespaçada:
                  no painel estreito a linha quebra, e três pastilhas soltas
                  custariam mais altura do que a informação vale. */}
                <span className="mt-[3px] flex flex-wrap items-center gap-x-1.5 font-mono text-apoio text-tinta-fraca">
                  <span>{dataHora(g.criado_em)}</span>
                  <span aria-hidden="true">·</span>
                  <span>{mmss(g.duracao_seg ?? 0)}</span>
                  {g.tem_video ? (
                    <>
                      <span aria-hidden="true">·</span>
                      <span className="text-verde-fundo">vídeo</span>
                    </>
                  ) : null}
                  {g.pendencias_abertas ? (
                    <>
                      <span aria-hidden="true">·</span>
                      <span className="text-ambar-escuro">
                        {g.pendencias_abertas} pendência{g.pendencias_abertas > 1 ? "s" : ""}
                      </span>
                    </>
                  ) : null}
                </span>
              </span>
              <Icone de={ICONES.avancar} tamanho={16} cor="var(--color-tinta-fraca)" estilo={s("margin-top:4px;flex:none")} />
            </button>
          ))
        : null}
    </>
  );
}
